import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import { tokenIsExpired } from '../../../authUtils';
import {
    Refresh, FileDownload
} from '@mui/icons-material';
import {
    Button, Tooltip, Box, Typography, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton
} from '@mui/material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const CustomerDueListReport = () => {
    const { isAuthenticated, companyId, branchId, token, logout, username, companyName } = useAuth();

    // Data states
    const [customerDueData, setCustomerDueData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    // Loading and error states
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Loading...');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, token, logout]);

    useEffect(() => {
        if (companyId && token) {
            fetchCustomerDueData();
        }
    }, [companyId, token]);

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    // Fetch customer due list data
    const fetchCustomerDueData = async () => {
        setCustomerDueData([]);
        let loadingTimeout;
        setLoadingMessage('Loading customer due list...');
        try {
            // Delay loading overlay by 300ms to prevent flash on quick loads
            loadingTimeout = setTimeout(() => {
                setLoading(true);
            }, 300);

            // Parameters: tmpType, toDate, companyId, itemId
            // Using 0 for itemId to get all items and current date
            const currentDate = getFormattedCurrentDate();
            console.log('Fetching customer due list with params:', {
                tmpType: 0,
                toDate: currentDate,
                companyId: companyId,
                itemId: 0
            });

            const response = await fetchData('ReportApi', `/CustomerDuelist/${currentDate}/${companyId}/0`, token);
            console.log('Customer Due List Response:', response);

            // Handle different response structures
            if (response) {
                if (response.responseCode === '1111') {
                    const data = response.response_data || [];
                    console.log('Customer Due Data:', data);
                    setCustomerDueData(data);
                } else if (Array.isArray(response)) {
                    // Direct array response
                    console.log('Direct array response:', response);
                    setCustomerDueData(response);
                } else if (response.response_data) {
                    // Has response_data but different response code
                    console.log('Response data without 1111 code:', response.response_data);
                    setCustomerDueData(response.response_data || []);
                } else {
                    console.log('Empty or unexpected response structure');
                    setCustomerDueData([]);
                }
            }
        } catch (error) {
            console.error('Error fetching customer due list:', error);
            setCustomerDueData([]);
        } finally {
            clearTimeout(loadingTimeout);
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Customer Due List');
        worksheet.views = [
            { state: 'frozen', xSplit: 0, ySplit: 3 }
        ];

        const now = new Date();
        const formattedDateTime = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
        const fileName = `Customer_Due_List_Report_${now.toLocaleDateString().replace(/\//g, '-')}.xlsx`;

        const thinBorder = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };

        // Company Name Row
        worksheet.mergeCells('A1:I1');
        const companyCell = worksheet.getCell('A1');
        companyCell.value = companyName || 'Company Name';
        companyCell.font = { bold: true, size: 14 };
        companyCell.alignment = { vertical: 'middle', horizontal: 'center' };

        // Title row
        worksheet.mergeCells('A2:I2');
        const titleCell = worksheet.getCell('A2');
        titleCell.value = `Customer Due List Report | Generated On: ${formattedDateTime} By: ${username}`;
        titleCell.font = { bold: true, size: 10 };
        titleCell.alignment = { vertical: 'middle', horizontal: 'left' };

        // Header row
        const headers = ['Sl.No', 'Sale Id', 'Bill Date', 'Bill Number', 'Customer Name', 'Sale Amount', 'Paid Amount', 'Balance Amount', 'Due Days'];
        const headerRow = worksheet.addRow(headers);

        headerRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF667eea' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = thinBorder;
        });

        // Calculate totals
        let totalSaleAmount = 0;
        let totalPaidAmount = 0;
        let totalBalanceAmount = 0;

        // Add data rows
        customerDueData.forEach((rowData, index) => {
            const saleAmount = parseFloat(rowData.SaleAmount) || 0;
            const paidAmount = parseFloat(rowData.PaidAmount) || 0;
            const balanceAmount = parseFloat(rowData.BalanceAmount) || 0;

            totalSaleAmount += saleAmount;
            totalPaidAmount += paidAmount;
            totalBalanceAmount += balanceAmount;

            const row = worksheet.addRow([
                index + 1,
                rowData.SaleId || '',
                rowData.BillDate ? formatDate(rowData.BillDate) : '',
                rowData.BillNumber || '',
                rowData.CustomerName || '',
                saleAmount,
                paidAmount,
                balanceAmount,
                rowData.DueDays || 0
            ]);

            row.eachCell((cell, colNumber) => {
                if (colNumber > 5) {
                    cell.alignment = { vertical: 'middle', horizontal: 'right' };
                    if (colNumber <= 8) {
                        cell.numFmt = '#,##0.00';
                    } else {
                        cell.numFmt = '#,##0';
                    }
                } else {
                    cell.alignment = { vertical: 'middle', horizontal: 'left' };
                }
                cell.border = thinBorder;
            });
        });

        // Add total row
        const totalRow = worksheet.addRow([
            '', '', '', '', 'Total:',
            totalSaleAmount,
            totalPaidAmount,
            totalBalanceAmount,
            ''
        ]);

        totalRow.eachCell((cell, colNumber) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
            if (colNumber === 5) {
                cell.alignment = { vertical: 'middle', horizontal: 'right' };
            } else if (colNumber > 5 && colNumber <= 8) {
                cell.alignment = { vertical: 'middle', horizontal: 'right' };
                cell.numFmt = '#,##0.00';
            }
            cell.border = thinBorder;
        });

        worksheet.columns = [
            { width: 8 },   // Sl.No
            { width: 12 },  // Sale Id
            { width: 15 },  // Bill Date
            { width: 18 },  // Bill Number
            { width: 30 },  // Customer Name
            { width: 15 },  // Sale Amount
            { width: 15 },  // Paid Amount
            { width: 15 },  // Balance Amount
            { width: 12 }   // Due Days
        ];

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), fileName);
    };

    // Calculate totals
    const calculateTotals = () => {
        let totalSaleAmount = 0;
        let totalPaidAmount = 0;
        let totalBalanceAmount = 0;

        customerDueData.forEach(customer => {
            totalSaleAmount += parseFloat(customer.SaleAmount) || 0;
            totalPaidAmount += parseFloat(customer.PaidAmount) || 0;
            totalBalanceAmount += parseFloat(customer.BalanceAmount) || 0;
        });

        return { totalSaleAmount, totalPaidAmount, totalBalanceAmount };
    };

    // Filter data based on search query
    const filteredCustomerDueData = customerDueData.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const totals = calculateTotals();

    return (
        <div style={{
            minWidth: '100%',
            background: '#f5f7fa',
            padding: isMobile ? '8px' : '10px',
            position: 'relative',
            paddingBottom: isMobile ? '80px' : '10px'
        }}>
            {/* Loading Overlay */}
            {loading && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        zIndex: '99999 !important',
                        backdropFilter: 'blur(4px)'
                    }}
                >
                    <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress
                            size={60}
                            thickness={4}
                            sx={{
                                color: '#667eea',
                                mb: 2
                            }}
                        />
                        <Typography
                            variant="h6"
                            sx={{
                                color: '#667eea',
                                fontWeight: 600,
                                fontSize: '1rem'
                            }}
                        >
                            {loadingMessage}
                        </Typography>
                    </Box>
                </Box>
            )}

            <div style={{
                maxWidth: '1400px',
                margin: '0 auto',
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                padding: isMobile ? '8px' : '12px',
                position: 'relative',
                color: 'black',
                fontFamily: 'Times New Roman',
            }}>
                {/* Header Section */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px',
                    paddingBottom: '8px',
                    borderBottom: '1px solid #f0f0f0',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '8px' : '0'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: isMobile ? '100%' : 'auto' }}>
                        <div style={{ width: '100%' }}>
                            <h4 style={{
                                fontSize: isMobile ? '15px' : '17px',
                                fontWeight: '700',
                                margin: 0,
                                letterSpacing: '-0.3px',
                                fontFamily: 'Times New Roman',
                                textTransform: 'uppercase',
                                textAlign: isMobile ? 'center' : 'left'
                            }}>
                                <span style={{ color: 'black', fontFamily: 'Times New Roman' }}>Customer Due List Report</span>
                            </h4>
                        </div>
                    </div>
                </div>

                {/* Filters Row */}
                <div style={{
                    marginBottom: '12px',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'thin'
                }}>
                    <div style={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'flex-end',
                        justifyContent: 'flex-end',
                        minWidth: 'fit-content',
                        paddingBottom: '4px',
                        flexWrap: 'nowrap'
                    }}>
                        {/* Search Input */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '140px' : '200px', flex: isMobile ? '1' : 'none' }}>
                            <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>Search</label>
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{
                                    width: '100%',
                                    borderRadius: '6px',
                                    backgroundColor: '#f8f9fa',
                                    fontSize: isMobile ? '10px' : '11px',
                                    height: '32px',
                                    border: '1px solid #d1d5db',
                                    padding: isMobile ? '8px 6px' : '8px 12px'
                                }}
                            />
                        </div>

                        {/* Buttons */}
                        {isMobile ? (
                            <>
                                {/* Refresh Icon Button - Mobile */}
                                <Tooltip title="Refresh">
                                    <IconButton
                                        onClick={fetchCustomerDueData}
                                        style={{
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            color: 'white',
                                            width: '32px',
                                            height: '32px',
                                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                                            padding: '4px'
                                        }}
                                    >
                                        <Refresh fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                {/* Export Icon Button - Mobile */}
                                <Tooltip title="Export">
                                    <IconButton
                                        onClick={handleExportExcel}
                                        style={{
                                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                            color: 'white',
                                            width: '32px',
                                            height: '32px',
                                            boxShadow: '0 2px 8px rgba(245, 158, 11, 0.4)',
                                            padding: '4px'
                                        }}
                                    >
                                        <FileDownload fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </>
                        ) : (
                            <>
                                {/* Refresh Button - Desktop */}
                                <Button
                                    onClick={fetchCustomerDueData}
                                    style={{
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: 'white',
                                        padding: '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '700',
                                        boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                                        border: 'none',
                                        height: '32px',
                                        textTransform: 'none',
                                        letterSpacing: '0.5px',
                                        minWidth: '100px'
                                    }}
                                >
                                    Refresh
                                </Button>

                                {/* Excel Button - Desktop */}
                                <Button
                                    onClick={handleExportExcel}
                                    style={{
                                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                        color: 'white',
                                        padding: '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '700',
                                        boxShadow: '0 2px 8px rgba(245, 158, 11, 0.4)',
                                        border: 'none',
                                        height: '32px',
                                        textTransform: 'none',
                                        letterSpacing: '0.5px',
                                        minWidth: '100px'
                                    }}
                                >
                                    Export Excel
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                {/* Table Section */}
                <div style={{ marginBottom: isMobile ? '80px' : '8px' }}>
                    <TableContainer component={Paper} style={{
                        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
                        borderRadius: '6px',
                        overflow: 'auto',
                        maxHeight: '70vh'
                    }}>
                        <Table stickyHeader sx={{ minWidth: isMobile ? 1000 : 650 }} size="small" aria-label="customer due list table">
                            <TableHead>
                                <TableRow style={{ height: '14px' }}>
                                    {['Sl.No', 'Sale Id', 'Bill Date', 'Bill Number', 'Customer Name', 'Sale Amount', 'Paid Amount', 'Balance Amount', 'Due Days'].map((col) => (
                                        <TableCell
                                            key={col}
                                            style={{
                                                background: 'linear-gradient(135deg, #6d4396ff 0%, #6d4396ff 100%)',
                                                color: 'white',
                                                fontWeight: '700',
                                                fontSize: isMobile ? '8px' : '9px',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                                padding: isMobile ? '6px 4px' : '8px 10px',
                                                textAlign: col === 'Sl.No' || col === 'Sale Id' || col === 'Bill Date' || col === 'Bill Number' || col === 'Customer Name' ? 'left' : 'right',
                                                whiteSpace: 'nowrap',
                                                borderBottom: 'none',
                                                borderRight: '1px solid rgba(255,255,255,0.15)'
                                            }}
                                        >
                                            {col}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {filteredCustomerDueData.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#6b7280' }}>
                                                No customer due records found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    <>
                                        {filteredCustomerDueData.map((data, index) => (
                                            <TableRow
                                                key={index}
                                                sx={{
                                                    '&:nth-of-type(even)': { backgroundColor: '#fafbfc' },
                                                    '&:hover': {
                                                        backgroundColor: '#f1f5f9',
                                                        transition: 'all 0.15s ease-in-out'
                                                    },
                                                    borderBottom: '1px solid #f1f5f9',
                                                    height: '28px'
                                                }}
                                            >
                                                <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75 }}>
                                                    <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 500, color: '#374151' }}>
                                                        {index + 1}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75 }}>
                                                    <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', color: '#374151' }}>
                                                        {data.SaleId || '-'}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75 }}>
                                                    <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', color: '#374151' }}>
                                                        {formatDate(data.BillDate)}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75 }}>
                                                    <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', color: '#374151' }}>
                                                        {data.BillNumber || '-'}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75 }}>
                                                    <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', color: '#374151' }}>
                                                        {data.CustomerName || '-'}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                    <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#1e40af' }}>
                                                        {(parseFloat(data.SaleAmount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                    <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#059669' }}>
                                                        {(parseFloat(data.PaidAmount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                    <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#dc2626' }}>
                                                        {(parseFloat(data.BalanceAmount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                    <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#f59e0b' }}>
                                                        {data.DueDays || '0'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {/* Total Row */}
                                        <TableRow
                                            sx={{
                                                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                                                borderTop: '2px solid #cbd5e1',
                                                '&:hover': {
                                                    background: 'linear-gradient(90deg, #5a6fd6 0%, #6a3d94 100%) !important'
                                                }
                                            }}
                                        >
                                            <TableCell colSpan={5} sx={{ py: 0.75, px: isMobile ? 0.5 : 0.75, textAlign: 'right', borderBottom: 'none' }}>
                                                <Typography variant="body2" sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem', fontWeight: 700, color: 'white' }}>
                                                    Total:
                                                </Typography>
                                            </TableCell>

                                            <TableCell sx={{ py: 0.75, px: isMobile ? 0.5 : 0.75, textAlign: 'right', borderBottom: 'none' }}>
                                                <Typography variant="body2" sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem', fontWeight: 700, color: 'white' }}>
                                                    {totals.totalSaleAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                            </TableCell>

                                            <TableCell sx={{ py: 0.75, px: isMobile ? 0.5 : 0.75, textAlign: 'right', borderBottom: 'none' }}>
                                                <Typography variant="body2" sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem', fontWeight: 700, color: 'white' }}>
                                                    {totals.totalPaidAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                            </TableCell>

                                            <TableCell sx={{ py: 0.75, px: isMobile ? 0.5 : 0.75, textAlign: 'right', borderBottom: 'none' }}>
                                                <Typography variant="body2" sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem', fontWeight: 700, color: '#dc2626' }}>
                                                    {totals.totalBalanceAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                            </TableCell>

                                            <TableCell sx={{ py: 0.75, px: isMobile ? 0.5 : 0.75 }}>
                                            </TableCell>
                                        </TableRow>
                                    </>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            </div>
        </div>
    );
};

export default CustomerDueListReport;
